import React, { useState, useEffect } from 'react';
import { getUserFiles, getDownloadUrl, deleteFile, debugFileVisibility, renameFile, getFileVersions, revertFilename, issueShareLink, sendShareEmail, updateFileTags, getUserTags, createTag, addTagToFiles } from '../../services/api';
import QuickEditModal from './QuickEditModal';

const S3FileManager = ({ refreshTrigger = 0, className = "", onFilesLoaded, onFileClick, externalFiles = [] }) => {
  const [files, setFiles] = useState([]); // combined list (local + external)
  const [baseFiles, setBaseFiles] = useState([]); // local S3 files only
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);
  const [downloadingFiles, setDownloadingFiles] = useState(new Set());
  const [deletingFiles, setDeletingFiles] = useState(new Set());
  const [fileToDelete, setFileToDelete] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState(new Set());
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [folders, setFolders] = useState([]);
  const [showTagModal, setShowTagModal] = useState(false);
  const [fileToTag, setFileToTag] = useState(null);
  const [availableTags, setAvailableTags] = useState([
    'Receipt', 'Invoice', 'Document', 'Image', 'Important', 'Archive', 'Personal', 'Business'
  ]);
  const [renamingFile, setRenamingFile] = useState(null);
  const [newFilename, setNewFilename] = useState('');
  const [versionFile, setVersionFile] = useState(null);
  const [versions, setVersions] = useState([]);
  const [lastSelectedIndex, setLastSelectedIndex] = useState(null);
  const [quickEditFile, setQuickEditFile] = useState(null);
  const [sharingFile, setSharingFile] = useState(null);
  const [shareEmail, setShareEmail] = useState('');
  const [shareMessage, setShareMessage] = useState('');
  const [shareExpires, setShareExpires] = useState(10080); // minutes (7 days)
  const [shareDownloads, setShareDownloads] = useState(1);
  const [sharePasscode, setSharePasscode] = useState('');
  const [shareResultUrl, setShareResultUrl] = useState('');
  const [shareBusy, setShareBusy] = useState(false);
  const [sendBusy, setSendBusy] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [actionsFile, setActionsFile] = useState(null);

  useEffect(() => {
    loadFiles();
    loadDebugInfo();
    const handler = (e) => {
      const f = e.detail && e.detail.file;
      if (f) openShareModal(f);
    };
    window.addEventListener('ordio-open-share', handler);
    return () => window.removeEventListener('ordio-open-share', handler);
  }, [refreshTrigger]);

  const loadFiles = async () => {
    try {
      setLoading(true);
      setError(null);
      const userFiles = await getUserFiles();
      // Map backend ai_tags -> tags for UI rendering consistency
      const mapped = (userFiles || []).map(f => ({ ...f, tags: Array.isArray(f.ai_tags) ? f.ai_tags : (f.tags || []) }));
      setBaseFiles(mapped);
      setFiles([...(mapped || []), ...(externalFiles || [])]);
      
      // Call the onFilesLoaded callback if provided
      if (onFilesLoaded && typeof onFilesLoaded === 'function') {
        onFilesLoaded(userFiles);
      }
    } catch (err) {
      setError(`Failed to load files: ${err.message}`);
      console.error('Error loading files:', err);
    } finally {
      setLoading(false);
    }
  };

  // When external files change (e.g., Drive), refresh combined list
  useEffect(() => {
    setFiles([...(baseFiles || []), ...(externalFiles || [])]);
  }, [externalFiles]);

  const loadDebugInfo = async () => {
    try {
      const debug = await debugFileVisibility();
      setDebugInfo(debug);
    } catch (err) {
      console.warn('Debug info failed:', err.message);
    }
  };

  const handleDownload = async (file) => {
    if (file?.source === 'drive' && file?.external_url) {
      window.open(file.external_url, '_blank', 'noopener,noreferrer');
      return;
    }
    const fileKey = file.file_key;
    setDownloadingFiles(prev => new Set([...prev, fileKey]));
    
    try {
      const { presigned_url } = await getDownloadUrl(fileKey);
      
      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = presigned_url;
      link.download = file.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
    } catch (err) {
      alert(`Download failed: ${err.message}`);
    } finally {
      setDownloadingFiles(prev => {
        const newSet = new Set(prev);
        newSet.delete(fileKey);
        return newSet;
      });
    }
  };

  const handleDeleteRequest = (file) => {
    setFileToDelete(file);
    setShowDeleteConfirm(true);
  };

  const openRenameModal = (file) => {
    setRenamingFile(file);
    setNewFilename(file.filename || '');
  };

  const openShareModal = (file) => {
    setSharingFile(file);
    setShareEmail('');
    setShareMessage('');
    setShareExpires(10080);
    setShareDownloads(1);
    setSharePasscode('');
    setShareResultUrl('');
  };

  const openActionsSheet = (file) => {
    setActionsFile(file);
    setShowActions(true);
  };

  const submitShare = async () => {
    if (!sharingFile) return;
    try {
      setShareBusy(true);
      const res = await issueShareLink({
        fileKey: sharingFile.file_key,
        recipientEmail: shareEmail || null,
        recipientName: null,
        message: shareMessage || null,
        expiresMinutes: Number(shareExpires) || 10080,
        maxDownloads: Number(shareDownloads) || 1,
        passcode: sharePasscode || null,
      });
      setShareResultUrl(res.share_url);
      // Do not auto-send. User can click Send Email.
    } catch (e) {
      alert(`Share failed: ${e.message}`);
    } finally {
      setShareBusy(false);
    }
  };

  const submitSendEmail = async () => {
    if (!shareResultUrl) {
      alert('Create the share link first.');
      return;
    }
    if (!shareEmail) {
      alert('Enter a recipient email.');
      return;
    }
    const tokenParam = new URL(shareResultUrl).searchParams.get('token');
    if (!tokenParam) {
      alert('Invalid share URL.');
      return;
    }
    try {
      setSendBusy(true);
      await sendShareEmail({ token: tokenParam, recipientEmail: shareEmail, recipientName: null, message: shareMessage || null });
      alert(`Email sent to ${shareEmail}`);
    } catch (e) {
      alert(`Failed to send email: ${e.message}`);
    } finally {
      setSendBusy(false);
    }
  };

  const applyRename = async () => {
    if (!renamingFile || !newFilename.trim()) return;
    try {
      await renameFile(renamingFile.upload_id, newFilename.trim());
      setFiles(prev => prev.map(f => f.upload_id === renamingFile.upload_id ? { ...f, filename: newFilename.trim() } : f));
      if (onFilesLoaded) onFilesLoaded(files.map(f => f.upload_id === renamingFile.upload_id ? { ...f, filename: newFilename.trim() } : f));
      setRenamingFile(null);
      setNewFilename('');
    } catch (e) {
      alert(`Rename failed: ${e.message}`);
    }
  };

  const cancelRename = () => {
    setRenamingFile(null);
    setNewFilename('');
  };

  const openVersionsModal = async (file) => {
    setVersionFile(file);
    try {
      const res = await getFileVersions(file.upload_id);
      setVersions(res.versions || []);
    } catch (e) {
      alert(`Failed to load versions: ${e.message}`);
    }
  };

  const handleRevertVersion = async (index) => {
    if (!versionFile) return;
    try {
      await revertFilename(versionFile.upload_id, index);
      // Refresh file list to reflect current name
      await loadFiles();
      setVersionFile(null);
      setVersions([]);
    } catch (e) {
      alert(`Revert failed: ${e.message}`);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!fileToDelete) return;
    
    const fileKey = fileToDelete.file_key;
    setDeletingFiles(prev => new Set([...prev, fileKey]));
    setShowDeleteConfirm(false);
    
    try {
      await deleteFile(fileKey);
      
      // Remove file from local state
      setFiles(prev => {
        const updatedFiles = prev.filter(f => f.file_key !== fileKey);
        
        // Update parent component's file list
        if (onFilesLoaded && typeof onFilesLoaded === 'function') {
          onFilesLoaded(updatedFiles);
        }
        
        return updatedFiles;
      });
      
      // Show success message
      alert('File deleted successfully!');
      
    } catch (err) {
      alert(`Delete failed: ${err.message}`);
    } finally {
      setDeletingFiles(prev => {
        const newSet = new Set(prev);
        newSet.delete(fileKey);
        return newSet;
      });
      setFileToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setFileToDelete(null);
  };

  // Bulk selection handlers
  const handleCheckboxChange = (index, file, e) => {
    const isSelected = e.target.checked;
    const isShift = e.nativeEvent && e.nativeEvent.shiftKey;

    setSelectedFiles(prev => {
      const newSelected = new Set(prev);

      if (isShift && lastSelectedIndex !== null && files.length > 0) {
        const start = Math.min(lastSelectedIndex, index);
        const end = Math.max(lastSelectedIndex, index);
        for (let i = start; i <= end; i++) {
          const fk = files[i]?.file_key;
          if (!fk) continue;
          if (isSelected) newSelected.add(fk);
          else newSelected.delete(fk);
        }
      } else {
        const fk = file.file_key;
        if (isSelected) newSelected.add(fk);
        else newSelected.delete(fk);
      }

      return newSelected;
    });

    setLastSelectedIndex(index);
  };

  const handleShiftToggleRow = (index, file) => {
    const currentlySelected = selectedFiles.has(file.file_key);
    const willSelect = !currentlySelected;
    setSelectedFiles(prev => {
      const newSelected = new Set(prev);
      if (lastSelectedIndex !== null && files.length > 0) {
        const start = Math.min(lastSelectedIndex, index);
        const end = Math.max(lastSelectedIndex, index);
        for (let i = start; i <= end; i++) {
          const fk = files[i]?.file_key;
          if (!fk) continue;
          if (willSelect) newSelected.add(fk);
          else newSelected.delete(fk);
        }
      } else {
        if (willSelect) newSelected.add(file.file_key);
        else newSelected.delete(file.file_key);
      }
      return newSelected;
    });
    setLastSelectedIndex(index);
  };

  const handleSelectAll = () => {
    if (selectedFiles.size === files.length) {
      // Deselect all
      setSelectedFiles(new Set());
    } else {
      // Select all
      setSelectedFiles(new Set(files.map(f => f.file_key)));
    }
  };

  const openQuickEdit = (file) => {
    setQuickEditFile(file);
  };

  const handleQuickEditSaved = (updated) => {
    setFiles(prev => prev.map(f => (f.upload_id === updated.upload_id ? { ...f, ...updated } : f)));
    if (onFilesLoaded) {
      const updatedFiles = files.map(f => (f.upload_id === updated.upload_id ? { ...f, ...updated } : f));
      onFilesLoaded(updatedFiles);
    }
  };

  const handleBulkDeleteRequest = () => {
    if (selectedFiles.size === 0) return;
    setShowBulkDeleteConfirm(true);
  };

  const handleBulkDeleteConfirm = async () => {
    if (selectedFiles.size === 0) return;
    
    setBulkDeleting(true);
    setShowBulkDeleteConfirm(false);
    
    const selectedArray = Array.from(selectedFiles);
    let successCount = 0;
    let errorCount = 0;
    
    // Delete files in parallel (but limit concurrency to avoid overwhelming the server)
    const deletePromises = selectedArray.map(async (fileKey) => {
      try {
        await deleteFile(fileKey);
        successCount++;
        
        // Remove from local state immediately upon success
        setFiles(prev => {
          const updatedFiles = prev.filter(f => f.file_key !== fileKey);
          
          // Update parent component's file list
          if (onFilesLoaded && typeof onFilesLoaded === 'function') {
            onFilesLoaded(updatedFiles);
          }
          
          return updatedFiles;
        });
        
        return { fileKey, success: true };
      } catch (err) {
        errorCount++;
        console.error(`Failed to delete ${fileKey}:`, err);
        return { fileKey, success: false, error: err.message };
      }
    });
    
    try {
      await Promise.all(deletePromises);
      
      // Clear selection
      setSelectedFiles(new Set());
      
      // Show summary
      if (errorCount === 0) {
        alert(`Successfully deleted ${successCount} file(s)!`);
      } else {
        alert(`Deleted ${successCount} file(s) successfully. ${errorCount} file(s) failed to delete.`);
      }
      
    } catch (err) {
      alert(`Bulk delete failed: ${err.message}`);
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleBulkDeleteCancel = () => {
    setShowBulkDeleteConfirm(false);
  };

  // Folder creation functions
  const handleCreateFolder = () => {
    setShowCreateFolder(true);
  };

  const handleFolderCreate = () => {
    if (!newFolderName.trim()) return;
    
    const newFolder = {
      id: Date.now().toString(),
      name: newFolderName.trim(),
      type: 'folder',
      created: new Date().toISOString(),
      files: []
    };
    
    setFolders(prev => [...prev, newFolder]);
    setNewFolderName('');
    setShowCreateFolder(false);
  };

  const handleFolderCancel = () => {
    setShowCreateFolder(false);
    setNewFolderName('');
  };

  // Tag functionality
  const handleTagFile = (file) => {
    setFileToTag(file);
    setShowTagModal(true);
  };

  const handleApplyTags = async (selectedTags) => {
    if (!fileToTag) return;
    try {
      // Persist human-readable tag names on the file for AI context
      await updateFileTags(fileToTag.upload_id, selectedTags);

      // Ensure tag entities exist and link them so Tag Manager usage reflects reality
      try {
        // Build name -> id map
        let existing = [];
        try { existing = await getUserTags(); } catch (_) { existing = []; }
        const nameToId = new Map((existing || []).map(t => [String(t.name || '').trim().toLowerCase(), t.tag_id]));
        const ensureId = async (name) => {
          const key = String(name || '').trim().toLowerCase();
          if (!key) return null;
          let id = nameToId.get(key);
          if (!id) {
            try { const res = await createTag({ name }); id = res?.tag_id; if (id) nameToId.set(key, id); } catch (_) {}
          }
          return id;
        };
        const tagIds = [];
        for (const n of (selectedTags || [])) { const id = await ensureId(n); if (id) tagIds.push(id); }
        for (const id of tagIds) { try { await addTagToFiles(id, [fileToTag.upload_id]); } catch (_) {} }
      } catch (_) {}

      // Update local UI state
      setFiles(prev => prev.map(file => 
        file.file_key === fileToTag.file_key 
          ? { ...file, tags: selectedTags }
          : file
      ));

      if (onFilesLoaded) {
        const updatedFiles = files.map(file => 
          file.file_key === fileToTag.file_key 
            ? { ...file, tags: selectedTags }
            : file
        );
        onFilesLoaded(updatedFiles);
      }
    } finally {
      setShowTagModal(false);
      setFileToTag(null);
    }
  };

  const handleTagCancel = () => {
    setShowTagModal(false);
    setFileToTag(null);
  };

  // Get source indicator
  const getSourceIcon = (file) => {
    if (file?.source === 'drive') {
      return (
        <div className="flex items-center space-x-1">
          {/* Google Drive icon (tri-color simplified) */}
          <svg className="w-4 h-4" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M7.5 3L2 12l3.5 6L11 9 7.5 3z" fill="#34a853"/>
            <path d="M16.5 3H7.5L11 9h9l-3.5-6z" fill="#fbbc05"/>
            <path d="M22 12H11l-5.5 6H18.5L22 12z" fill="#4285f4"/>
          </svg>
          <span className="text-xs text-gray-600 dark:text-gray-400">Google Drive</span>
        </div>
      );
    }
    return (
      <div className="flex items-center space-x-1">
        <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
        <span className="text-xs text-gray-600 dark:text-gray-400">Local</span>
      </div>
    );
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFileIcon = (filename) => {
    const ext = filename?.split('.').pop()?.toLowerCase();
    
    switch (ext) {
      case 'pdf':
        return (
          <svg className="w-8 h-8 text-red-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
          </svg>
        );
      case 'jpg':
      case 'jpeg':
      case 'png':
        return (
          <svg className="w-8 h-8 text-green-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
          </svg>
        );
      case 'doc':
      case 'docx':
        return (
          <svg className="w-8 h-8 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
          </svg>
        );
      case 'csv':
      case 'xls':
      case 'xlsx':
        return (
          <svg className="w-8 h-8 text-green-700" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v1a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" clipRule="evenodd" />
          </svg>
        );
      default:
        return (
          <svg className="w-8 h-8 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
          </svg>
        );
    }
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center p-8 ${className}`}>
        <div className="flex items-center space-x-2">
          <svg className="animate-spin -ml-1 mr-3 h-6 w-6 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="text-gray-600">Loading files...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`p-4 ${className}`}>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading files</h3>
              <p className="mt-2 text-sm text-red-700">{error}</p>
              <button 
                onClick={loadFiles}
                className="mt-2 text-sm text-red-800 underline hover:text-red-900"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>


      {/* Files Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Your Files ({files.length})
          </h3>
          {selectedFiles.size > 0 && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {selectedFiles.size} selected
              </span>
              <button
                onClick={handleBulkDeleteRequest}
                disabled={bulkDeleting}
                className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-red-900 dark:text-red-300"
              >
                {bulkDeleting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-1 h-3 w-3 text-red-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Deleting...
                  </>
                ) : (
                  <>
                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" clipRule="evenodd" />
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 012 0v4a1 1 0 11-2 0V7zM12 7a1 1 0 10-2 0v4a1 1 0 002 0V7z" clipRule="evenodd" />
                    </svg>
                    Delete Selected
                  </>
                )}
              </button>
            </div>
          )}
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={handleCreateFolder}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-blue-900 dark:text-blue-300"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            New Folder
          </button>
          <button
            onClick={loadFiles}
            className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Files Table / Cards */}
      {files.length === 0 && folders.length === 0 ? (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
            <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No files</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Upload your first file to get started!</p>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
          {/* Select All Section */}
          {files.length > 0 && (
            <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={selectedFiles.size === files.length && files.length > 0}
                  onChange={handleSelectAll}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Select All ({files.length} files)
                </label>
              </div>
            </div>
          )}

          {/* Table (desktop) */}
          <div className="overflow-x-auto hidden md:block">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-8">
                    <span className="sr-only">Select</span>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    File
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Date Uploaded
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Source
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Shared With
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {/* Render Folders First */}
                {folders.map((folder) => (
                  <tr key={folder.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 w-8 h-8">
                          <svg className="w-8 h-8 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {folder.name}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            Folder • {folder.files.length} files
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(folder.created)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                        Local
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      Only you
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300">
                          Open
                        </button>
                        <button className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300">
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}

                {/* Render Files */}
                {files.map((file, index) => (
                  <tr key={file.upload_id || index} className={`hover:bg-gray-50 dark:hover:bg-gray-700 ${selectedFiles.has(file.file_key) ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedFiles.has(file.file_key)}
                        onChange={(e) => handleCheckboxChange(index, file, e)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 w-8 h-8">
                          {getFileIcon(file.filename)}
                        </div>
                        <div className="ml-3 flex-1">
                          <div 
                            className="text-sm font-medium text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 cursor-pointer transition-colors"
                            onClick={(e) => {
                              if (e.shiftKey) {
                                // Use shift-click to range toggle selection instead of opening preview
                                e.preventDefault();
                                handleShiftToggleRow(index, file);
                                return;
                              }
                              if (onFileClick) onFileClick(file);
                              else openQuickEdit(file);
                            }}
                            title="Click to preview file"
                          >
                            {file.filename || 'Unnamed file'}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center space-x-2">
                            <span>{formatFileSize(file.size)}</span>
                            {file.parent_name && (
                              <>
                                <span>•</span>
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7h4l2 2h10a2 2 0 012 2v6a2 2 0 01-2 2H3a2 2 0 01-2-2V9a2 2 0 012-2z" /></svg>
                                  {file.parent_name}
                                  {file.source === 'drive' && (
                                    <svg className="w-3 h-3 ml-1" viewBox="0 0 24 24" aria-hidden="true"><path d="M7.5 3L2 12l3.5 6L11 9 7.5 3z" fill="#34a853"/><path d="M16.5 3H7.5L11 9h9l-3.5-6z" fill="#fbbc05"/><path d="M22 12H11l-5.5 6H18.5L22 12z" fill="#4285f4"/></svg>
                                  )}
                                </span>
                              </>
                            )}
                            {file.tags && file.tags.length > 0 && (
                              <>
                                <span>•</span>
                                <div className="flex flex-wrap gap-1">
                                  {file.tags.slice(0, 2).map((tag, tagIndex) => (
                                    <span key={tagIndex} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">
                                      {tag}
                                    </span>
                                  ))}
                                  {file.tags.length > 2 && (
                                    <span className="text-xs text-gray-400">+{file.tags.length - 2}</span>
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(file.last_modified)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getSourceIcon(file)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      Only you
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => openQuickEdit(file)}
                          className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                          title="Quick edit"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 20h9" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16.5 3.5a2.121 2.121 0 113 3L7 19l-4 1 1-4 12.5-12.5z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleTagFile(file)}
                          className="text-purple-600 hover:text-purple-900 dark:text-purple-400 dark:hover:text-purple-300"
                          title="Add tags"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => openRenameModal(file)}
                          className="text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
                          title="Rename"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5M16.5 3.5a2.121 2.121 0 113 3L12 14l-4 1 1-4 7.5-7.5z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => openVersionsModal(file)}
                          className="text-teal-600 hover:text-teal-900 dark:text-teal-400 dark:hover:text-teal-300"
                          title="Version history"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDownload(file)}
                          disabled={downloadingFiles.has(file.file_key) || deletingFiles.has(file.file_key)}
                          className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 disabled:opacity-50"
                        >
                          {downloadingFiles.has(file.file_key) ? (
                            <svg className="animate-spin w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                          ) : (
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          )}
                        </button>
                        <button
                          onClick={() => openShareModal(file)}
                          className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
                          title="Share"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 12v.01M12 20a8 8 0 100-16 8 8 0 000 16zm5-9l-6 3m0-6l6 3" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDeleteRequest(file)}
                          disabled={downloadingFiles.has(file.file_key) || deletingFiles.has(file.file_key)}
                          className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-50"
                        >
                          {deletingFiles.has(file.file_key) ? (
                            <svg className="animate-spin w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                          ) : (
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" clipRule="evenodd" />
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 012 0v4a1 1 0 11-2 0V7zM12 7a1 1 0 10-2 0v4a1 1 0 002 0V7z" clipRule="evenodd" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Cards (mobile) */}
          <div className="md:hidden p-3 grid grid-cols-1 gap-3">
            {files.map((file) => (
              <div key={file.upload_id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 flex items-center justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-gray-100 dark:bg-gray-700 rounded">
                    {getFileIcon(file.filename)}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{file.filename}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{formatFileSize(file.size)} • {formatDate(file.last_modified)}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => openActionsSheet(file)} className="p-2 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200" title="More">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zm6 0a2 2 0 11-4 0 2 2 0 014 0zm6 0a2 2 0 11-4 0 2 2 0 014 0z"/></svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bottom Action Sheet (mobile) */}
      {showActions && actionsFile && (
        <div className="fixed inset-0 z-50 md:hidden" onClick={() => setShowActions(false)}>
          <div className="absolute inset-0 bg-black bg-opacity-40" />
          <div className="absolute bottom-0 left-0 right-0 bg-white dark:bg-gray-800 rounded-t-xl p-4 shadow-2xl" onClick={(e)=>e.stopPropagation()}>
            <div className="text-sm text-gray-500 dark:text-gray-400 mb-2 truncate">{actionsFile.filename}</div>
            <div className="grid grid-cols-4 gap-3 text-center">
              <button onClick={()=>{ openShareModal(actionsFile); setShowActions(false); }} className="flex flex-col items-center p-2 rounded bg-gray-50 dark:bg-gray-700">
                <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 12v.01M12 20a8 8 0 100-16 8 8 0 000 16zm5-9l-6 3m0-6l6 3"/></svg>
                <span className="text-xs mt-1">Share</span>
              </button>
              <button onClick={()=>{ handleDownload(actionsFile); setShowActions(false); }} className="flex flex-col items-center p-2 rounded bg-gray-50 dark:bg-gray-700">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
                <span className="text-xs mt-1">Download</span>
              </button>
              <button onClick={()=>{ openQuickEdit(actionsFile); setShowActions(false); }} className="flex flex-col items-center p-2 rounded bg-gray-50 dark:bg-gray-700">
                <svg className="w-6 h-6 text-gray-700 dark:text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 20h9"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16.5 3.5a2.121 2.121 0 113 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>
                <span className="text-xs mt-1">Edit</span>
              </button>
              <button onClick={()=>{ handleDeleteRequest(actionsFile); setShowActions(false); }} className="flex flex-col items-center p-2 rounded bg-gray-50 dark:bg-gray-700">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 012 0v4a1 1 0 11-2 0V7zM12 7a1 1 0 10-2 0v4a1 1 0 002 0V7z"/></svg>
                <span className="text-xs mt-1">Delete</span>
              </button>
            </div>
            <button onClick={()=>setShowActions(false)} className="mt-3 w-full py-2 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200">Close</button>
          </div>
        </div>
      )}

      {/* Sticky bottom nav (mobile) */}
      <div className="fixed bottom-0 inset-x-0 md:hidden bg-white/90 dark:bg-gray-900/90 backdrop-blur border-t border-gray-200 dark:border-gray-800 z-40">
        <div className="flex justify-around py-2 text-xs">
          <a href="#" className="flex flex-col items-center text-blue-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7h18M5 7v10a2 2 0 002 2h10a2 2 0 002-2V7"/></svg>
            <span>Files</span>
          </a>
          <a href="/settings" className="flex flex-col items-center text-gray-600 dark:text-gray-300">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 17a4 4 0 100-8 4 4 0 000 8z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
            <span>Settings</span>
          </a>
        </div>
      </div>

      {/* Floating Upload FAB (mobile) */}
      <button
        onClick={() => window.dispatchEvent(new CustomEvent('ordio-open-upload'))}
        className="md:hidden fixed bottom-16 right-4 z-40 rounded-full bg-blue-600 text-white shadow-lg w-12 h-12 flex items-center justify-center"
        title="Upload"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"/></svg>
      </button>
      
      

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && fileToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.732 15.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900">Delete File</h3>
              </div>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-gray-500">
                Are you sure you want to delete <strong>{fileToDelete.filename}</strong>? 
                This action cannot be undone.
              </p>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleDeleteCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition duration-200"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Dialog */}
      {showBulkDeleteConfirm && selectedFiles.size > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.732 15.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900">Delete Multiple Files</h3>
              </div>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-gray-500 mb-3">
                Are you sure you want to delete <strong>{selectedFiles.size} file(s)</strong>? 
                This action cannot be undone.
              </p>
              
              {/* Show list of selected files */}
              <div className="max-h-32 overflow-y-auto bg-gray-50 rounded p-2">
                {Array.from(selectedFiles).map((fileKey) => {
                  const file = files.find(f => f.file_key === fileKey);
                  return (
                    <div key={fileKey} className="text-xs text-gray-600 py-1">
                      • {file?.filename || 'Unknown file'}
                    </div>
                  );
                })}
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleBulkDeleteCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkDeleteConfirm}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition duration-200"
              >
                Delete {selectedFiles.size} File(s)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Folder Modal */}
      {showCreateFolder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Create New Folder</h3>
              </div>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Folder Name
              </label>
              <input
                type="text"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="Enter folder name"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleFolderCreate();
                  }
                }}
                autoFocus
              />
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleFolderCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 rounded-md transition duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleFolderCreate}
                disabled={!newFolderName.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed rounded-md transition duration-200"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tag Modal */}
      {showTagModal && fileToTag && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Add Tags</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">{fileToTag.filename}</p>
              </div>
            </div>
            
            <TagSelector
              availableTags={availableTags}
              selectedTags={fileToTag.tags || []}
              onTagsChange={handleApplyTags}
              onCancel={handleTagCancel}
            />
          </div>
        </div>
      )}

      {/* Rename Modal */}
      {renamingFile && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-gray-700 dark:text-gray-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5M16.5 3.5a2.121 2.121 0 113 3L12 14l-4 1 1-4 7.5-7.5z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Rename File</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">{renamingFile.filename}</p>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">New filename</label>
              <input
                type="text"
                value={newFilename}
                onChange={(e) => setNewFilename(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                onKeyPress={(e) => { if (e.key === 'Enter') applyRename(); }}
                autoFocus
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button onClick={cancelRename} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 rounded-md transition duration-200">Cancel</button>
              <button onClick={applyRename} disabled={!newFilename.trim()} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed rounded-md transition duration-200">Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Versions Modal */}
      {versionFile && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-lg w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Filename Versions</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Current: {versionFile.filename}</p>
              </div>
            </div>
            {versions.length === 0 ? (
              <div className="text-sm text-gray-500 dark:text-gray-400">No previous versions</div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {versions.map((v, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 rounded border border-gray-200 dark:border-gray-700">
                    <div>
                      <div className="text-sm text-gray-900 dark:text-gray-100">{v.filename}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Changed at: {new Date(v.changed_at).toLocaleString()}</div>
                    </div>
                    <button onClick={() => handleRevertVersion(idx)} className="px-3 py-1 text-xs rounded bg-teal-600 hover:bg-teal-700 text-white">Revert</button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-end mt-4">
              <button onClick={() => { setVersionFile(null); setVersions([]); }} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 rounded-md transition duration-200">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Edit Modal */}
      {quickEditFile && (
        <QuickEditModal
          file={quickEditFile}
          onClose={() => setQuickEditFile(null)}
          onSaved={handleQuickEditSaved}
        />
      )}

      {/* Share Modal */}
      {sharingFile && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="mb-3">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Share File</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">{sharingFile.filename}</p>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">Recipient Email (optional)</label>
                <input type="email" value={shareEmail} onChange={(e)=>setShareEmail(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white" placeholder="name@example.com"/>
              </div>
              <div>
                <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">Message (optional)</label>
                <textarea value={shareMessage} onChange={(e)=>setShareMessage(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white" rows={3} />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">Expires (min)</label>
                  <input type="number" min={5} value={shareExpires} onChange={(e)=>setShareExpires(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white" />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">Max downloads</label>
                  <input type="number" min={1} value={shareDownloads} onChange={(e)=>setShareDownloads(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white" />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">Passcode (opt)</label>
                  <input type="text" value={sharePasscode} onChange={(e)=>setSharePasscode(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white" />
                </div>
              </div>

              {shareResultUrl && (
                <div className="bg-gray-50 dark:bg-gray-700 p-2 rounded">
                  <label className="block text-xs text-gray-500 dark:text-gray-300">Share URL</label>
                  <div className="flex items-center space-x-2 mt-1">
                    <input readOnly value={shareResultUrl} className="flex-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-white text-xs" />
                    <button onClick={()=>{ navigator.clipboard.writeText(shareResultUrl); }} className="px-2 py-1 text-xs bg-blue-600 text-white rounded">Copy</button>
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end space-x-2 mt-4">
              <button onClick={()=>setSharingFile(null)} className="px-3 py-2 text-sm bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded">Close</button>
              <button onClick={submitShare} disabled={shareBusy} className="px-3 py-2 text-sm bg-indigo-600 text-white rounded disabled:opacity-50">{shareBusy ? 'Sharing...' : 'Create Link'}</button>
              <button onClick={submitSendEmail} disabled={sendBusy} className="px-3 py-2 text-sm bg-blue-600 text-white rounded disabled:opacity-50">{sendBusy ? 'Sending…' : 'Send Email'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Tag Selector Component
const TagSelector = ({ availableTags, selectedTags, onTagsChange, onCancel }) => {
  const [currentSelected, setCurrentSelected] = useState(selectedTags);
  const [newTag, setNewTag] = useState('');

  const handleTagToggle = (tag) => {
    setCurrentSelected(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const handleAddNewTag = () => {
    if (newTag.trim() && !availableTags.includes(newTag.trim())) {
      const trimmedTag = newTag.trim();
      setCurrentSelected(prev => [...prev, trimmedTag]);
      setNewTag('');
    }
  };

  const handleApply = () => {
    onTagsChange(currentSelected);
  };

  return (
    <div>
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Available Tags
        </label>
        <div className="flex flex-wrap gap-2 mb-3">
          {availableTags.map((tag) => (
            <button
              key={tag}
              onClick={() => handleTagToggle(tag)}
              className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                currentSelected.includes(tag)
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500'
              }`}
            >
              {tag}
            </button>
          ))}
        </div>
        
        <div className="flex space-x-2">
          <input
            type="text"
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            placeholder="Create new tag"
            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm"
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleAddNewTag();
              }
            }}
          />
          <button
            onClick={handleAddNewTag}
            disabled={!newTag.trim()}
            className="px-3 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 disabled:text-gray-400"
          >
            Add
          </button>
        </div>
      </div>

      {currentSelected.length > 0 && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Selected Tags ({currentSelected.length})
          </label>
          <div className="flex flex-wrap gap-2">
            {currentSelected.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300"
              >
                {tag}
                <button
                  onClick={() => handleTagToggle(tag)}
                  className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-blue-200 dark:hover:bg-blue-800"
                >
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="flex justify-end space-x-3">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 rounded-md transition duration-200"
        >
          Cancel
        </button>
        <button
          onClick={handleApply}
          className="px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-md transition duration-200"
        >
          Apply Tags
        </button>
      </div>
    </div>
  );
};

export default S3FileManager;

